import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import DatePicker from "react-datepicker";
import cn from "classnames";

import { useAppSelector, useAppDispatch } from "store/index";
import { handleUpdateFields } from "store/liveOps/promocodes/slice";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import { getDatePickerFormats, useTimeFormat } from "helpers/timeFormat";

interface PromocodesMainTabProps {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  isEditMode: boolean;
  readOnly?: boolean;
}

const PromocodesMainTab: React.FC<PromocodesMainTabProps> = ({
  fieldErrors,
  clearFieldError,
  isEditMode,
  readOnly
}) => {
  const { t } = useTranslation();
  const datePickerFormats = getDatePickerFormats(useTimeFormat());
  const dispatch = useAppDispatch();

  const payload = useAppSelector((store) => store.promocodes.payload);

  const handleData = (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: img ?? value }));
  };

  return (
    <div className="promocodes-form-content">
      <div className="bonus-form-box">
        <div className="bonus-form-box--item">
          <InnerInput
            value={payload?.title ?? ""}
            placeholder={t("title")}
            label={t("title")}
            type="text"
            id="promocode-title"
            onChange={(e) => {
              handleData("title", e);
              clearFieldError("title");
            }}
            hasError={Boolean(fieldErrors?.["title"])}
            disabled={readOnly}
          />
        </div>

        <div className="bonus-form-box--item">
          <InnerInput
            maxCodePoints={2500}
            value={payload?.description ?? ""}
            placeholder={t("description")}
            label={t("description")}
            type="textarea"
            id="promocode-description"
            onChange={(e) => handleData("description", e)}
            disabled={readOnly}
          />
        </div>

        <div className="bonus-form-box--item bonus-form-box--row">
          <DefaultDropdown
            value={payload?.activationArea}
            placeholder={t("activationArea")}
            label={t("activationArea")}
            options={[
              { value: "signup", label: "signup" },
              { value: "bonus", label: "bonus" },
              { value: "deposit", label: "deposit" },
            ]}
            onSelect={(e) => {
              handleData("activationArea", e);
              clearFieldError("activationArea");
            }}
            hasError={Boolean(fieldErrors?.["activationArea"])}
            disabled={isEditMode || readOnly}
          />

          <DefaultDropdown
            value={payload?.applicationMode}
            placeholder={t("applicationMode")}
            label={t("applicationMode")}
            options={[
              { value: "grant", label: "grant" },
              { value: "enhance", label: "enhance" },
            ]}
            onSelect={(e) => {
              handleData("applicationMode", e);
              clearFieldError("applicationMode");
            }}
            hasError={Boolean(fieldErrors?.["applicationMode"])}
            disabled={isEditMode || readOnly}
          />
        </div>

        <div className="bonus-form-box--item bonus-form-box--row">
          <div
            className={cn("date-picker-wrapper", {
              "has-error": fieldErrors?.["startsAt"],
            })}
          >
            <div className="title">{t("startedAt")}</div>

            <DatePicker
              customInput={<ReadOnlyInput />}
              locale="en-GB"
              showTimeSelect
              timeFormat={datePickerFormats.timeFormat}
              placeholderText={datePickerFormats.placeholder}
              dateFormat={datePickerFormats.dateFormat}
              selected={
                payload?.startsAt
                  ? moment(
                      payload.startsAt,
                      [moment.ISO_8601, "DD.MM.YYYY HH:mm"],
                      true,
                    ).toDate()
                  : null
              }
              onChange={(date) => {
                if (!date) return;

                handleData("startsAt", moment(date).format("DD.MM.YYYY HH:mm"));
                clearFieldError("startsAt");
              }}
              disabled={readOnly}
            />
          </div>

          <div
            className={cn("date-picker-wrapper", {
              "has-error": fieldErrors?.["endsAt"],
            })}
          >
            <div className="title">{t("finishedAt")}</div>

            <DatePicker
              customInput={<ReadOnlyInput />}
              locale="en-GB"
              showTimeSelect
              timeFormat={datePickerFormats.timeFormat}
              placeholderText={datePickerFormats.placeholder}
              dateFormat={datePickerFormats.dateFormat}
              selected={
                payload?.endsAt
                  ? moment(
                      payload.endsAt,
                      [moment.ISO_8601, "DD.MM.YYYY HH:mm"],
                      true,
                    ).toDate()
                  : null
              }
              onChange={(date) => {
                if (!date) return;

                handleData("endsAt", moment(date).format("DD.MM.YYYY HH:mm"));
                clearFieldError("endsAt");
              }}
              disabled={readOnly}
            />
          </div>
        </div>
      </div>

      <div className="bonus-form-box">
        <div className="bonus-form-box--item">
          <InnerInput
            value={payload?.code ?? ""}
            placeholder={t("code")}
            label={t("code")}
            type="text"
            id="promocode-code"
            disabled={isEditMode || readOnly}
            onChange={(e) => {
              handleData("code", e);
              clearFieldError("code");
            }}
            hasError={Boolean(fieldErrors?.["code"])}
          />
        </div>

        <div className="bonus-form-box--item bonus-form-box--row">
          <InnerInput
            value={payload?.maxUsedCount?.toString() ?? ""}
            placeholder={t("maxUsedCount")}
            label={t("maxUsedCount")}
            type="number"
            id="promocode-max-count"
            onChange={(e) => {
              handleData("maxUsedCount", e);
              clearFieldError("maxUsedCount");
            }}
            hasError={Boolean(fieldErrors?.["maxUsedCount"])}
            disabled={readOnly}
          />

          <InnerInput
            value={payload?.maxUsesPerUser?.toString() ?? ""}
            placeholder={t("maxUsesPerUser")}
            label={t("maxUsesPerUser")}
            type="number"
             id="promocode-max-uses"
            disabled={isEditMode || readOnly}
            onChange={(e) => {
              handleData("maxUsesPerUser", e);
              clearFieldError("maxUsesPerUser");
            }}
            hasError={Boolean(fieldErrors?.["maxUsesPerUser"])}
          />
        </div>

        <div className="bonus-form-box--item bonus-form-box--row">
          <DefaultDropdown
            value={payload?.type}
            placeholder={t("type")}
            label={t("type")}
            options={[{ value: "bonus", label: "bonus" }]}
            onSelect={(e) => {
              handleData("type", e);
              clearFieldError("type");
            }}
            hasError={Boolean(fieldErrors?.["type"])}
            disabled={isEditMode || readOnly}
          />

          <DefaultDropdown
            value={payload?.targetDomain}
            placeholder={t("targetDomain")}
            label={t("targetDomain")}
            options={[{ value: "bonus", label: "bonus" }]}
            onSelect={(e) => {
              handleData("targetDomain", e);
              clearFieldError("targetDomain");
            }}
            hasError={Boolean(fieldErrors?.["targetDomain"])}
            disabled={readOnly}
          />
        </div>

        <div className="bonus-form-switcher">
          <SwitchBtn
            activeTxt={t("active")}
            isActive={!!payload?.isActive}
            handleClick={() =>
              handleData("isActive", !!payload?.isActive ? 0 : 1)
            }
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
};

export default PromocodesMainTab;
